import { SharedLanguageService } from '@language';
import { ApiQuery } from '../../core/api/entities/api-query.entity';
import { EmptyStructuredQuery } from '../../core/query/entities/empty-structured-query.constant';
import { StructuredQuery } from '../../core/query/entities/structured-query.entity';
import { UrlQueryParams } from '../../core/query/entities/url-query-params.entity';
import { UrlQueryParamsConverter } from '../../core/query/helpers/url-query-params-converter.helper';
import { ShFacet } from '../../elements/components/facet/entities/facet.entity';
import { LogicalOperand, Operand } from '../../public_api';
import { COMPLETABLE_TASK_FACET } from '../constants/virtual-facets.constants';
import { FacetSelectionType } from '../entities/facet-request.entity';

/**
 * The "Mijn taken" filter, which no facet profile lists — the Taken panel synthesises it.
 *
 * One factory for two readers: the panel renders it as a facet, and the active-filter bar reads it as a
 * {@link VirtualFacetField} descriptor, a shape {@link ShFacet} satisfies. A second copy would let the pill
 * and the filter it stands for disagree on their labels.
 */
export function CompletableTaskFacet(translateService: SharedLanguageService): ShFacet {
    return {
        key: COMPLETABLE_TASK_FACET,
        label: translateService.instant('facets.tasks.only-completable.label'),
        hasMoreValues: false,
        values: [
            {
                value: 'true',
                label: translateService.instant('facets.tasks.only-completable.values.true'),
                count: undefined as never as number,
                selectionType: FacetSelectionType.MUST
            },
            {
                value: 'false',
                label: translateService.instant('facets.tasks.only-completable.values.false'),
                count: undefined as never as number,
                selectionType: FacetSelectionType.MUST
            }
        ],
        selectionType: FacetSelectionType.MUST
    };
}

export function ExtractOnlyCompletableValue(query: ApiQuery | null, defaultValue: boolean): boolean {
    if (query === null) {
        return true;
    }
    const onlyCompletableFacet = query
        .toApiStructuredQuery()
        .Facets.find(facet => facet.DottedKey === COMPLETABLE_TASK_FACET);
    const onlyCompletableRawValue = onlyCompletableFacet?.Buckets?.[0].Value?.toLocaleLowerCase();
    if (onlyCompletableRawValue !== undefined) {
        return onlyCompletableRawValue === 'true';
    }
    const onlyCompletableQueryPart = query.queryParts.find(qp => qp.field === COMPLETABLE_TASK_FACET)?.values[0];
    return onlyCompletableQueryPart !== undefined ? onlyCompletableQueryPart === 'true' : defaultValue;
}

export function GetCompletableFacetUrlQueryParams(): UrlQueryParams {
    const structuredQuery: StructuredQuery = {
        ...EmptyStructuredQuery,
        Facets: [
            {
                LogicalOperand: LogicalOperand.And,
                DottedKey: COMPLETABLE_TASK_FACET,
                Buckets: [
                    {
                        Operand: Operand.Include,
                        Value: 'true'
                    }
                ]
            }
        ]
    };
    return UrlQueryParamsConverter.generateQueryParams(structuredQuery);
}
